import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components, GroupBase, GroupProps } from 'react-select';
import { Box } from '@mui/material';
import { getMultiSelectCustomStyles, MultiSelectLabel } from './MultiSelect.styled';
import { MultiSelectOption, MultiSelectProps } from './MultiSelect.types';
import { CompactMultiValue } from './CompactMultiValue';

export const MultiSelect: React.FC<MultiSelectProps> = ({
  placeholder,
  width = 330,
  value,
  label,
  options,
  onChange,
  deferred,
}) => {
  const [localValue, setLocalValue] = useState<MultiSelectOption[]>(deferred ? value : []);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentValueRef = useRef<MultiSelectOption[]>(value);

  const isSame = (a: MultiSelectOption[], b: MultiSelectOption[]) =>
    JSON.stringify(a) === JSON.stringify(b);

  useEffect(() => {
    if (!deferred) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (!isSame(localValue, lastSentValueRef.current)) {
          onChange(localValue);
          lastSentValueRef.current = localValue;
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [deferred, localValue, onChange]);

  useEffect(() => {
    if (!deferred) {
      setLocalValue(value);
    }
  }, [value, deferred]);

  const handleChange = (options: MultiSelectOption[]) => {
    if (deferred) {
      setLocalValue(options);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (!isSame(options, lastSentValueRef.current)) {
          onChange(options);
          lastSentValueRef.current = options;
        }
      }, 2000);
    } else {
      onChange(options);
    }
  };

  const customGrouping = {
    Group: (props: GroupProps<MultiSelectOption, true>) => {
      const allGroups = props.selectProps.options as GroupBase<MultiSelectOption>[];
      const currentGroup = props.data;
      const groupIndex = allGroups.findIndex((group) => group.label === currentGroup.label);

      return (
        <>
          {groupIndex > 0 && <div style={{ borderTop: '1px solid #ddd', margin: '4px 0' }} />}
          <components.Group {...props} />
        </>
      );
    },
    GroupHeading: () => null,
  };

  const customStyles = useMemo(() => getMultiSelectCustomStyles(width), [width]);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      {label && <MultiSelectLabel>{label}</MultiSelectLabel>}
      <Select
        isMulti
        value={deferred ? localValue : value}
        onChange={handleChange}
        options={options}
        styles={customStyles}
        placeholder={placeholder}
        closeMenuOnSelect={false}
        menuPortalTarget={document.body}
        menuPlacement='bottom'
        hideSelectedOptions={false}
        components={{
          ...customGrouping,
          MultiValue: CompactMultiValue,
        }}
      />
    </Box>
  );
};
